/**
 * HipRecordsList - the merged OPD visits + IPD admissions list of "Link health records to ABHA"
 * (redesign v2, variant A). Self-loading: owns the one-row filters (search, date popover, visit type,
 * status, refresh), the GET /hip/records fetch, the count line and the pager. Each row is one
 * HipEncounterCard.
 *
 * Props: { patientId?, onPatient?, onError? } (build-plan-redesign R-F9). No props = the main list. With `patientId` it is the
 * PATIENT mode of the patient page (OR-75, A4.3), see the end of this comment.
 *
 * Request discipline (no storms):
 *   - ALL filter state is ONE object (`query`), changed by ONE setState, and a filter change
 *     always resets the page in that same update - so one change = one render = one fetch
 *     (never "new filter with old page", then "page 1"), even from the search box's timer.
 *   - The fetch effect depends on [query, refreshTick] only: `query` is state (same reference until
 *     set), `refreshTick` is a counter.
 *   - Latest request wins: an effect cleanup marks the earlier request stale.
 *   - A custom date range is typed into a DRAFT (popover state, not `query`) and sent only on Apply;
 *     the draft is checked with the same rules the server enforces, so an invalid range sends
 *     NOTHING (Apply is off, the reason is shown). `query` therefore never holds an invalid range.
 *   - Re-selecting the already-selected option is a no-op. The pager + Refresh are disabled while
 *     a request is in flight (the link notice's background refresh, OR-114, is the exception: it neither dims nor locks).
 *     The previous list stays mounted while a refetch runs (dimmed),
 *     so open cards keep their state when a card calls onChanged() and across the background refresh.
 *
 * Opens on the status "Not linked yet" (OR-66; the server includes ABHA-not-verified patients, OR-68) and on the
 * date preset "Last 3 days" (OR-96, temporary - see DEFAULT_PRESET).
 * Filters (status, visit type, dates) are mirrored in the URL search params so Back from the patient
 * page restores them: READ ONCE on mount, WRITTEN with `replace` on a filter change, NEVER read back,
 * so URL <-> state cannot loop. The search text and the page are not mirrored.
 *
 * PATIENT mode (`patientId`): every visit and admission of ONE patient for the last 12 months (the server's range; it
 * is shown as a line, there is no date popover and no search). Status opens on "All statuses" and a visit type + a
 * status select filter it. Rows are grouped under month headers (IST). It shows the newest PATIENT_PAGE_SIZE and
 * "Show older" APPENDS the next page. `query` is then { kind, status, pages, append }:
 *   - append=false: pages 1..`pages` in ONE request (page 1, page_size = size * pages). A filter change resets to
 *     pages=1; a reload (a card write, the refresh button, Retry) keeps `pages` while size * pages fits the server's
 *     page_size cap (50), else it goes back to 1. The link notice's background refresh (OR-114) is the one reload that
 *     NEVER goes back to 1: `pages` stays, it asks for the newest 50 rows (the cap) and the older rows on screen are kept.
 *   - append=true ("Show older"): only page `pages` (page_size = size) is fetched and added to the list.
 * One user action is still one request, latest wins. No URL reads or writes (the page owns the address).
 * The patient block of each response goes to `onPatient(patient)` (the page draws its header from it, so the page
 * needs no request of its own); a failed request goes to `onError(err)` (the page shows "not found" for a 404).
 * Both are read through refs: a new callback identity never refetches. The page keys this component by patientId.
 *
 * The link notice (OR-93, amended by OR-114): a card that linked something reports { patientName, records } through onChanged
 * (records = [{ encounterKind, encounterId, slotKey | invoiceId, name, state }]). The notice lists each record with the state
 * the server last gave it (LINKING "sent to ABHA, linking…", LINKED "linked", LINK_FAILED / PAUSED "could not be linked"), is STICKY
 * (closes on its x, on the next link, or when a filter changes - never on a timer), and every response of THIS list refreshes it
 * (noticeFromRows). While a tracked record is LINKING the list refreshes ITSELF (`refreshQuietly`: the same fetch, no second path): the next
 * refresh is scheduled POLL_MS after the previous response settled - so never two in flight - and only up to POLL_MAX_MS after the
 * link (the server fails an unanswered link at 2 minutes), and not after a failed load. OR-117: when a tracked record is still LINKING
 * and its card is not in the rows (a busy list: the card left it or moved to another page, so the rows cannot say), the SAME tick asks
 * that patient's own records (the patient view's request, status All, newest 50) after the list answer - one request per patient, one
 * after the other, `loading` held until the last, so the whole tick is single-flight; a failed lookup keeps the last known state.
 * The schedule is one effect on primitives
 * (stillLinking, loading, failed, noticeUntil); its timer is cleared on every dep change, on close, on replace and on unmount.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import HipEncounterCard from './HipEncounterCard';
import { NOT_APPLICABLE_LOOK } from './HipRecordTypeStrip';
import HipSearchBox from '../HipSearchBox';
import { listRecords } from '../../../services/api/hipRecordsApi';
import { LIST, PATIENT, STRIP } from '../../../constants/hipRecordsCopy';
import {
  LIST_STATUS, LIST_STATUS_OPTIONS, NOTICE_OUTCOME, SLOT_STATE, noticeOutcome,
} from '../../../constants/hipRecordEnums';
import { getTodayIST } from '../../../utils/dateIST';
import { formatDateIST, formatDayIST, formatTimeIST, utcIsoToISTInputValue } from '../../../utils/formatIST';
import { formatPeriodLabel } from '../../../utils/reportArchive';
import { useTheme } from '../../../theme/ThemeContext';

const PAGE_SIZE = 20; // contract 4.1 default; the mockup pages by 20
const PATIENT_PAGE_SIZE = 10; // patient mode: the newest 10, then "Show older" adds 10 (mockup 1d)
const MAX_PAGE_SIZE = 50; // contract 4.1 / A4.3: page_size 1..50
const MAX_SPAN_DAYS = 31; // contract 4.1 / server _validate_range_and_page: (to - from) + 1 <= 31
const ALL = 'ALL'; // the "All" visit type (status "All statuses" is LIST_STATUS.ALL)
const POLL_MS = 5000; // OR-114: while a record of the link notice is LINKING, refresh this long after the last response
const POLL_MAX_MS = 120000; // ... for at most this long after the link (the server fails an unanswered link at 2 minutes)
const DAY_MS = 86400000;

// Contract `counts` keys. Status counts respect the kind filter; opd/ipd/all do not.
const KIND_COUNT_KEY = { ALL: 'all', OPD: 'opd', IPD: 'ipd' };
const STATUS_COUNT_KEY = {
  [LIST_STATUS.NOT_LINKED_YET]: 'notLinkedYet',
  [LIST_STATUS.LINK_FAILED]: 'linkFailed',
  [LIST_STATUS.NOT_VERIFIED]: 'notVerified',
  [LIST_STATUS.LINKED]: 'linked',
};

// ── IST calendar-day arithmetic (pure calendar math on YYYY-MM-DD; no clock, no time zone) ──
const pad2 = (n) => String(n).padStart(2, '0');

function dayNumber(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS : NaN;
}

function shiftDay(ymd, days) {
  const d = new Date((dayNumber(ymd) + days) * DAY_MS);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** '' when the range is valid; else the plain-words reason (no request is sent). */
function rangeProblem(from, to) {
  const a = dayNumber(from);
  const b = dayNumber(to);
  if (Number.isNaN(a) || Number.isNaN(b)) return LIST.rangeInvalid;
  if (b < a) return LIST.rangeOrder;
  if (b - a + 1 > MAX_SPAN_DAYS) return LIST.rangeSpan(MAX_SPAN_DAYS);
  return '';
}

// Keys = LIST.presets keys. "Last 7 days" = 7 days ago .. today (mockup: 23 Oct .. 30 Oct).
// "Last 3 days" = 2 days ago .. today, inclusive (OR-96).
function presetRange(key, today) {
  if (key === 'today') return { dateFrom: today, dateTo: today };
  if (key === 'yesterday') return { dateFrom: shiftDay(today, -1), dateTo: shiftDay(today, -1) };
  if (key === 'last3') return { dateFrom: shiftDay(today, -2), dateTo: today };
  return { dateFrom: shiftDay(today, -7), dateTo: today };
}

// OR-96 (TEMPORARY): the list opens on "Last 3 days" so the first load is small. REVERT when bk-2256 (list speed) lands:
// set this back to 'last7' and delete the LIST.last3Hint line under the filter bar.
const DEFAULT_PRESET = 'last3';

function initialQuery() {
  return {
    ...presetRange(DEFAULT_PRESET, getTodayIST()), kind: ALL, status: LIST_STATUS.NOT_LINKED_YET, q: '', page: 1,
  };
}

// ── Patient mode (OR-75) ──────────────────────────────────────────────────────────────────
const initialPatientQuery = () => ({ kind: ALL, status: LIST_STATUS.ALL, pages: 1, append: false });

/** The listRecords arguments of a query. Patient mode sends the patient and no dates (A4.3). */
function requestOf(query, patientId) {
  if (!patientId) return { ...query, pageSize: PAGE_SIZE };
  const { kind, status, pages, append } = query;
  // Not append: pages 1..`pages` in one request, capped at the server's page_size (only the notice's background refresh can ask
  // for more than the cap, with `pages` kept: it reloads the newest rows and joinRows keeps the older ones already on screen).
  return append
    ? { patientId, kind, status, page: pages, pageSize: PATIENT_PAGE_SIZE }
    : { patientId, kind, status, page: 1, pageSize: Math.min(PATIENT_PAGE_SIZE * pages, MAX_PAGE_SIZE) };
}

/** OR-117: the request that asks for ONE patient's own records (the patient view's shape: status All, the newest 50 = the server's
 *  cap in one request) to read a tracked record that the list cannot show. */
const patientCheckRequest = (patientId) => requestOf(
  { kind: ALL, status: LIST_STATUS.ALL, pages: MAX_PAGE_SIZE / PATIENT_PAGE_SIZE, append: false }, patientId,
);

/** `head` rows, then the `tail` rows that are not already in `head` (keyed like the cards, so a row that shifted between pages is
 *  never shown twice). "Show older": head = on screen, tail = the next page. A capped reload (patient view, more than
 *  MAX_PAGE_SIZE rows shown, the notice's background refresh): head = the fresh newest rows, tail = the rows on screen (older;
 *  their state is as of the last full load). */
function joinRows(head, tail) {
  const key = (r) => `${r.encounterKind}:${r.encounterId}`;
  const covered = new Set(head.map(key));
  return [...head, ...tail.filter((r) => !covered.has(key(r)))];
}

/** Rows come newest first, so a month is one run of rows: group them in order. key = IST month "YYYY-MM". */
function groupByMonth(records) {
  const groups = [];
  records.forEach((r) => {
    const key = utcIsoToISTInputValue(r.encounterAt).slice(0, 7);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.records.push(r);
    else groups.push({ key, records: [r] });
  });
  return groups;
}

// ── The link notice (OR-114) ────────────────────────────────────────────────────────────────
const isRowOf = (rec) => (r) => r.encounterKind === rec.encounterKind && r.encounterId === rec.encounterId;
/** The slot state a response gives a tracked record, or undefined: the card or slot is not in the rows (an invoice is found by
 *  invoiceId, the rest by slotKey), or the slot is READY (not an answer, so it never takes a record back from LINKED). */
function answeredState(rec, rows) {
  const slot = rows.find(isRowOf(rec))?.slots.find((s) => (rec.invoiceId ? s.invoice?.invoiceId === rec.invoiceId : s.slotKey === rec.slotKey));
  return slot && slot.state !== SLOT_STATE.READY ? slot.state : undefined;
}
/** The notice with each tracked record's state replaced by `stateOf(rec)` (pure; the same object back when nothing changed). */
function withStates(notice, stateOf) {
  let changed = false;
  const records = notice.records.map((rec) => {
    const state = stateOf(rec);
    if (state === rec.state) return rec;
    changed = true;
    return { ...rec, state };
  });
  return changed ? { ...notice, records } : notice;
}

/**
 * The notice's tracked records, read from a fresh response of the list. A record whose card is in the rows takes its slot's
 * state. A card that is NOT in the rows says something only on the default "Not linked yet" list and only when EVERY row the
 * filter matches is on screen (`totalCount <= records.length`, main list and patient view alike - a card can be missing from a
 * page because rows shifted between pages, OR-116 / QA D3): a card with a LINKING or LINK_FAILED record stays in that list
 * (OR-113, OR-111), so a LINKING record whose card left it was answered LINKED. Anywhere else a missing card says nothing here:
 * the record keeps its last known state (patientsToCheck then asks for it, OR-117).
 */
function noticeFromRows(notice, res, query) {
  const cardLeft = res.totalCount <= res.records.length && query.status === LIST_STATUS.NOT_LINKED_YET;
  return withStates(notice, (rec) => {
    const answered = answeredState(rec, res.records);
    if (answered) return answered;
    return cardLeft && !res.records.some(isRowOf(rec)) && noticeOutcome(rec.state) === NOTICE_OUTCOME.LINKING ? SLOT_STATE.LINKED : rec.state;
  });
}

/** OR-117: the patients to ask about - those with a tracked record still LINKING (after noticeFromRows) whose card is not in the
 *  rows, so the list could not tell (a busy list: the card left it, or moved to another page). Each patient once. */
function patientsToCheck(notice, res) {
  const ids = notice.records
    .filter((rec) => rec.patientId && noticeOutcome(rec.state) === NOTICE_OUTCOME.LINKING && !res.records.some(isRowOf(rec)))
    .map((rec) => rec.patientId);
  return [...new Set(ids)];
}
/** ... and what that patient's own records say: the real state of that patient's tracked records (a record not found keeps its state). */
const noticeFromPatientRows = (notice, patientId, rows) => withStates(notice, (rec) => (
  rec.patientId === patientId ? answeredState(rec, rows) ?? rec.state : rec.state));

// The notice's lines: one per outcome, could not be linked first, then linking, then linked (LIST.linkNoticeLine words each).
const NOTICE_ORDER = [NOTICE_OUTCOME.FAILED, NOTICE_OUTCOME.LINKING, NOTICE_OUTCOME.LINKED];
const noticeLines = ({ patientName, time, records }) => NOTICE_ORDER
  .map((outcome) => ({ outcome, names: records.filter((r) => noticeOutcome(r.state) === outcome).map((r) => r.name) }))
  .filter(({ names }) => names.length > 0)
  .map(({ outcome, names }) => LIST.linkNoticeLine(patientName, time, outcome, ...names));

// Neutral while anything is still linking, red if any record could not be linked, green when every record is linked.
function noticeTone(records) {
  const outcomes = records.map((r) => noticeOutcome(r.state));
  if (outcomes.includes(NOTICE_OUTCOME.FAILED)) return 'bad';
  return outcomes.includes(NOTICE_OUTCOME.LINKING) ? 'info' : 'ok';
}

// ── Filters <-> URL (read once, write with replace, never read back) ───────────────────────
/** The mount query: the defaults, with each valid URL value laid over them. Anything unknown is ignored. */
function queryFromSearch(search) {
  const base = initialQuery();
  const params = new URLSearchParams(search);
  const status = params.get('status');
  const kind = params.get('kind');
  const from = params.get('from');
  const to = params.get('to');
  return {
    ...base,
    status: LIST_STATUS_OPTIONS.includes(status) ? status : base.status,
    kind: Object.prototype.hasOwnProperty.call(LIST.kinds, kind) ? kind : base.kind,
    ...(rangeProblem(from, to) ? {} : { dateFrom: from, dateTo: to }),
  };
}

const searchFromQuery = (q) => `?${new URLSearchParams({
  status: q.status, kind: q.kind, from: q.dateFrom, to: q.dateTo,
})}`;

// ── Look (mockup variant A tokens; dark = html.dark via the app theme). SOLID fills only. ──
function palette(isDark) {
  return isDark
    ? {
      text: '#f1f5f9', muted: '#9ca3af', card: '#1e293b', input: '#0f172a', border: '#475569',
      btn: '#334155', btnText: '#e2e8f0', teal: '#0f766e', onTeal: '#ffffff', bar: '#111827',
      warnBg: '#713f12', warnText: '#fef08a', warnBorder: '#fbbf24', bad: '#f87171',
      okBg: '#14532d', okText: '#bbf7d0', okBorder: '#22c55e',
      infoBg: '#0c4a6e', infoText: '#bae6fd', infoBorder: '#38bdf8',
      badBg: '#7f1d1d', badText: '#fecaca', badBorder: '#f87171',
      inAppBg: '#64748b', inAppText: '#ffffff',
    }
    : {
      text: '#111827', muted: '#6b7280', card: '#ffffff', input: '#ffffff', border: '#9ca3af',
      btn: '#f1f5f9', btnText: '#1f2937', teal: '#0f766e', onTeal: '#ffffff', bar: '#f1f5f9',
      warnBg: '#fef9c3', warnText: '#854d0e', warnBorder: '#b45309', bad: '#dc2626',
      okBg: '#dcfce7', okText: '#166534', okBorder: '#16a34a',
      infoBg: '#e0f2fe', infoText: '#075985', infoBorder: '#0369a1',
      badBg: '#fee2e2', badText: '#991b1b', badBorder: '#dc2626',
      inAppBg: '#475569', inAppText: '#ffffff',
    };
}

const withCount = (label, count) => (count == null ? label : `${label} (${count})`);

export default function HipRecordsList({ patientId, onPatient, onError }) {
  const { isDark } = useTheme();
  const p = palette(isDark);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate); // a ref: the URL write must not re-run when the router re-renders
  navigateRef.current = navigate;
  // Refs, not effect deps: a new callback identity (a re-render of the page) must never refetch.
  const onPatientRef = useRef(onPatient);
  onPatientRef.current = onPatient;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const [query, setQuery] = useState(() => (patientId ? initialPatientQuery() : queryFromSearch(window.location.search)));
  const urlSearch = useRef(patientId ? null : searchFromQuery(query)); // what the URL says (or will after the last write)
  const [refreshTick, setRefreshTick] = useState(0);
  const [loaded, setLoaded] = useState(null); // { res, query } of the last good response
  const [loading, setLoading] = useState(true);
  const [quiet, setQuiet] = useState(false); // the request in flight is the notice's background refresh (OR-114): nothing dims or locks
  const quietNext = useRef(false); // set by the refresh timer, taken by the fetch effect
  const [failed, setFailed] = useState(false);
  const [linkNotice, setLinkNotice] = useState(null); // { patientName, time, records, until } after a link (OR-93, OR-114)
  const noticeRef = useRef(null); // the notice on screen, for the fetch below (it must not depend on it)
  noticeRef.current = linkNotice;
  const [dateOpen, setDateOpen] = useState(false);
  const [custom, setCustom] = useState(false); // the From/To inputs are showing
  const [draft, setDraft] = useState({ from: '', to: '' }); // typed range; sent only on Apply
  const dateRef = useRef(null);

  /** ONE state update per user change; a filter change always lands on page 1 (patient mode: back to one page, replaced). */
  const changeFilter = useCallback((patch) => setQuery((prev) => (
    Object.keys(patch).every((k) => prev[k] === patch[k])
      ? prev
      : { ...prev, ...patch, ...(patientId ? { pages: 1, append: false } : { page: 1 }) }
  )), [patientId]);
  const onSearch = useCallback((q) => changeFilter({ q }), [changeFilter]);
  const goToPage = (page) => setQuery((prev) => ({ ...prev, page }));
  // Patient mode: reload the pages already shown in ONE request (page 1, page_size = size * pages) while that fits the
  // server's cap, else start again from the newest page. (A new `query` object re-runs the fetch; the tick does not exist there.)
  const refresh = useCallback(() => {
    if (patientId) {
      setQuery((prev) => ({
        ...prev, append: false, pages: PATIENT_PAGE_SIZE * prev.pages <= MAX_PAGE_SIZE ? prev.pages : 1,
      }));
    } else {
      setRefreshTick((n) => n + 1);
    }
  }, [patientId]);
  // The link notice's background refresh (OR-114): the same fetch, but it never shrinks what the user has loaded. Patient view:
  // `pages` stays (a user's refresh above may start again from the newest page when size * pages passes the server's cap; this
  // one must not, it would collapse 60 rows to 10 every 5 s and wipe an open panel on the older rows) - see requestOf / keepTail.
  const refreshQuietly = useCallback(() => {
    quietNext.current = true;
    if (patientId) setQuery((prev) => ({ ...prev, append: false }));
    else setRefreshTick((n) => n + 1);
  }, [patientId]);
  const showOlder = () => setQuery((prev) => ({ ...prev, pages: prev.pages + 1, append: true }));
  const hideNotice = useCallback(() => setLinkNotice(null), []);
  /** A card reports a write. `info` ({ patientName, records }) is optional: only a link has it. A new link replaces the notice
   *  at once (OR-93) and starts its own 2-minute refresh window (OR-114); the refresh below is the first read of its records. */
  const onCardChanged = useCallback((info) => {
    if (info && info.records.length > 0) {
      setLinkNotice({
        patientName: info.patientName,
        time: formatTimeIST(new Date().toISOString()),
        records: info.records,
        until: Date.now() + POLL_MAX_MS,
      });
    }
    refresh();
  }, [refresh]);

  useEffect(() => {
    let stale = false; // latest request wins
    const isQuiet = quietNext.current; // this request is the notice's background refresh
    quietNext.current = false;
    setQuiet(isQuiet);
    setLoading(true);
    setFailed(false);
    listRecords(requestOf(query, patientId))
      .then((res) => {
        if (stale) return undefined;
        const lastPage = Math.max(1, Math.ceil(res.totalCount / (res.pageSize || PAGE_SIZE)));
        if (!patientId && query.page > lastPage) {
          // The set shrank under us (e.g. the last card on the last page was linked while a
          // status filter is on): go to the real last page. Strictly decreasing, so it ends.
          setQuery((prev) => (prev.page > lastPage ? { ...prev, page: lastPage } : prev));
          return undefined;
        }
        // "Show older" adds its page to the rows on screen; every other fetch replaces them. The newest response
        // supplies the totals and counts.
        setLoaded((prev) => {
          let records = res.records;
          if (prev && query.append) records = joinRows(prev.res.records, res.records);
          else if (prev && patientId && PATIENT_PAGE_SIZE * query.pages > MAX_PAGE_SIZE) records = joinRows(res.records, prev.res.records);
          return { res: records === res.records ? res : { ...res, records }, query };
        });
        // OR-114: the tracked records of the link notice read their state from these rows ("Show older" fetches only older rows).
        if (!query.append) setLinkNotice((n) => (n ? noticeFromRows(n, res, query) : n));
        if (res.patient) onPatientRef.current?.(res.patient);
        // OR-117: in the notice's background tick, a tracked record whose card the rows cannot show is looked up in ITS PATIENT's
        // records - one request per patient, one after the other and only after this list answer, so never two in flight; `loading`
        // stays on until the last answer, so the next tick waits for it. A failed lookup keeps the last known state, quietly.
        const notice = isQuiet && !query.append && noticeRef.current ? noticeFromRows(noticeRef.current, res, query) : null;
        const patients = notice ? patientsToCheck(notice, res) : [];
        if (patients.length === 0) {
          setLoading(false);
          return undefined;
        }
        return patients
          .reduce((chain, id) => chain.then(async () => {
            if (stale) return;
            try {
              const answer = await listRecords(patientCheckRequest(id));
              if (!stale) setLinkNotice((n) => (n ? noticeFromPatientRows(n, id, answer.records) : n));
            } catch (_err) { /* keep the last known state */ }
          }), Promise.resolve())
          .then(() => { if (!stale) setLoading(false); });
      })
      .catch((err) => {
        if (stale) return;
        if (isQuiet) {
          // A background refresh that failed must not wipe the list (cards keep their open panels and ticks): keep it as it
          // is and end the notice's refreshing (no retry loop); the notice keeps the last states it knew.
          setLinkNotice((n) => (n ? { ...n, until: 0 } : n));
          setLoading(false);
          return;
        }
        setLoaded(null);
        setFailed(true);
        setLoading(false);
        onErrorRef.current?.(err);
      });
    return () => { stale = true; };
  }, [query, refreshTick, patientId]);

  // OR-114: while a tracked record of the link notice is LINKING, refresh the list (the same fetch as the button, `refreshQuietly`; the
  // fetch above reads the states). The timer exists only while NO request is in flight (`loading`), so refreshes never overlap and the next one
  // comes POLL_MS after the last response; none after the notice's window (`noticeUntil`), after a failed load, once nothing is
  // LINKING, or once the notice is closed / replaced (every dep change clears the timer; so does unmount).
  const noticeUntil = linkNotice?.until;
  const stillLinking = !!linkNotice && linkNotice.records.some((r) => noticeOutcome(r.state) === NOTICE_OUTCOME.LINKING);
  useEffect(() => {
    if (!stillLinking || loading || failed || Date.now() + POLL_MS > noticeUntil) return undefined;
    const timer = setTimeout(refreshQuietly, POLL_MS);
    return () => clearTimeout(timer);
  }, [stillLinking, loading, failed, noticeUntil, refreshQuietly]);

  // URL <- filters. Primitive deps (a page change or the clamp above does not touch the URL); a write only when the
  // text differs from what the URL already says, so the mount (nothing changed) writes nothing. Patient mode: never.
  useEffect(() => {
    if (patientId) return;
    const search = searchFromQuery({
      status: query.status, kind: query.kind, dateFrom: query.dateFrom, dateTo: query.dateTo,
    });
    if (search === urlSearch.current) return;
    urlSearch.current = search;
    navigateRef.current({ search }, { replace: true });
  }, [patientId, query.status, query.kind, query.dateFrom, query.dateTo]);

  // A filter change clears the link notice (the card it spoke of has left the list or the list changed).
  // Primitive deps only: the page clamp, a page change and the card refetch (refreshTick) keep it.
  useEffect(() => { hideNotice(); }, [hideNotice, query.status, query.kind, query.dateFrom, query.dateTo, query.q]);

  // The date popover closes on Esc and on a click outside it.
  useEffect(() => {
    if (!dateOpen) return undefined;
    const onDown = (e) => { if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setDateOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [dateOpen]);

  const today = getTodayIST();
  const activePreset = Object.keys(LIST.presets).find((key) => {
    const r = presetRange(key, today);
    return r.dateFrom === query.dateFrom && r.dateTo === query.dateTo;
  });
  const draftProblem = rangeProblem(draft.from, draft.to);

  const toggleDate = () => {
    if (!dateOpen) { // seed the draft ONCE per open, from the live filter
      setDraft({ from: query.dateFrom, to: query.dateTo });
      setCustom(!activePreset);
    }
    setDateOpen(!dateOpen);
  };
  const pickPreset = (key) => {
    changeFilter(presetRange(key, getTodayIST()));
    setDateOpen(false);
  };
  const applyCustom = () => {
    if (draftProblem) return;
    changeFilter({ dateFrom: draft.from, dateTo: draft.to });
    setDateOpen(false);
  };

  const busy = loading && !quiet; // what the user sees and is locked by: a background refresh is neither
  // Everything below the filters describes the response on screen (`loaded`), not the live
  // filter state, so a number never mixes two queries while a refetch is in flight.
  const res = loaded?.res;
  const shown = loaded?.query;
  const counts = res?.counts;
  const kindCountOf = (kind) => (counts ? counts[KIND_COUNT_KEY[kind]] : null);
  const kindCount = counts ? kindCountOf(shown.kind) : null;
  const statusCountOf = (status) => {
    if (!counts) return null;
    return status === LIST_STATUS.ALL ? kindCount : counts[STATUS_COUNT_KEY[status]];
  };

  const records = res?.records || [];
  const totalPages = res ? Math.max(1, Math.ceil(res.totalCount / (res.pageSize || PAGE_SIZE))) : 1;
  const rangeText = shown && !patientId
    ? LIST.rangeText(formatDayIST(shown.dateFrom), formatDayIST(shown.dateTo))
    : '';
  const notLinkedYet = shown?.status === LIST_STATUS.NOT_LINKED_YET;
  let countText = '';
  if (shown && patientId) {
    countText = PATIENT.countLine(res.totalCount, records.length);
  } else if (shown) {
    countText = notLinkedYet
      ? LIST.needLinkingLine(res.totalCount, LIST.rangeDash(formatDayIST(shown.dateFrom), formatDayIST(shown.dateTo)))
      : LIST.countLine(
        kindCount,
        rangeText,
        shown.status === LIST_STATUS.ALL ? null : LIST.statuses[shown.status],
        res.totalCount,
      );
  }
  // Patient mode: the range is the server's 12 months, not a filter.
  const patientRange = shown && patientId
    ? PATIENT.range12(LIST.rangeDash(formatDayIST(res.dateFrom), formatDayIST(res.dateTo)))
    : '';
  const remaining = res ? res.totalCount - records.length : 0;

  const inputStyle = {
    background: p.input, color: p.text, border: `1px solid ${p.border}`, borderRadius: 6,
    padding: '7px 9px', fontSize: 13, colorScheme: isDark ? 'dark' : 'light',
  };
  const selectStyle = { ...inputStyle, flex: '1 1 150px', minWidth: 0, maxWidth: '100%', cursor: 'pointer' };
  const buttonStyle = (disabled) => ({
    fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 6, whiteSpace: 'nowrap',
    background: p.btn, color: p.btnText, border: `1px solid ${p.border}`,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
  });
  const optionStyle = (on) => ({
    display: 'block', width: '100%', textAlign: 'left', fontSize: 13, fontWeight: 600, padding: '7px 9px',
    marginBottom: 4, borderRadius: 6, cursor: 'pointer',
    border: `1px solid ${on ? p.teal : p.border}`, background: on ? p.teal : p.card, color: on ? p.onTeal : p.text,
  });
  const noteStyle = { textAlign: 'center', padding: '2rem 1rem', color: p.muted };
  const legendShape = { borderRadius: 99, padding: '2px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' };
  const legendPill = ({ background, color, border }) => ({
    ...legendShape, background, color, border: `1px solid ${border}`,
  });

  // The live filters in words, handed by the card's "View all records" link to the patient page (PATIENT.filtersKept).
  // Patient mode: the card hides that link.
  const listFilters = patientId ? undefined : [
    LIST.statuses[query.status],
    activePreset ? LIST.presets[activePreset] : LIST.rangeDash(formatDayIST(query.dateFrom), formatDayIST(query.dateTo)),
    LIST.kindsKept[query.kind],
  ];

  // The link notice's solid fill in the tone of what is true now (OR-114): see noticeTone.
  const noticeLook = linkNotice && {
    ok: { bg: p.okBg, text: p.okText, border: p.okBorder },
    info: { bg: p.infoBg, text: p.infoText, border: p.infoBorder },
    bad: { bg: p.badBg, text: p.badText, border: p.badBorder },
  }[noticeTone(linkNotice.records)];

  const renderCard = (r) => (
    <HipEncounterCard
      key={`${r.encounterKind}:${r.encounterId}`}
      record={r}
      onChanged={onCardChanged}
      inPatientView={!!patientId}
      listFilters={listFilters}
    />
  );

  return (
    <div>
      {/* Sticky one-row filter bar: a SOLID fill in both themes (cards scroll under it). It is also the
          anchor of the date popover. */}
      <div
        data-testid="list-filter-bar"
        style={{
          position: 'sticky', top: 0, zIndex: 20, background: p.bar, borderBottom: `1px solid ${p.border}`,
          padding: '10px 12px', margin: '0 -12px 10px',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          {patientId ? (
            // Patient mode: no search, no date popover - the 12-month range is a line, not a filter.
            patientRange && (
              <span
                data-testid="patient-range"
                style={{ ...inputStyle, fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                {patientRange}
              </span>
            )
          ) : (
            <>
              <HipSearchBox label={LIST.searchPlaceholder} onSearch={onSearch} />

              <div ref={dateRef}>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={dateOpen}
                  onClick={toggleDate}
                  style={{ ...inputStyle, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {LIST.dateButton(
                    LIST.presets[activePreset],
                    LIST.rangeDash(formatDayIST(query.dateFrom), formatDayIST(query.dateTo)),
                  )}
                  {' ▾'}
                </button>
                {dateOpen && (
                  <div
                    role="dialog"
                    aria-label={LIST.dateLabel}
                    style={{
                      position: 'absolute', zIndex: 30, top: 'calc(100% - 4px)', left: 12, width: 300, maxWidth: 'calc(100% - 24px)',
                      background: p.card, color: p.text, border: `1px solid ${p.border}`, borderRadius: 10, padding: 12,
                      boxShadow: '0 10px 28px rgba(0,0,0,0.25)',
                    }}
                  >
                    <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: p.muted }}>
                      {LIST.dateLabel}
                    </p>
                    {Object.entries(LIST.presets).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={key === activePreset}
                        onClick={() => pickPreset(key)}
                        style={optionStyle(key === activePreset)}
                      >
                        {label}
                      </button>
                    ))}
                    <button
                      type="button"
                      aria-pressed={custom}
                      onClick={() => setCustom(true)}
                      style={optionStyle(custom)}
                    >
                      {LIST.customRange}
                    </button>
                    {custom && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 8, margin: '4px 0 8px' }}>
                        <label style={{ fontSize: 12, color: p.muted, display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 110px' }}>
                          {LIST.from}
                          <input
                            type="date"
                            value={draft.from}
                            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                            style={inputStyle}
                          />
                        </label>
                        <label style={{ fontSize: 12, color: p.muted, display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 110px' }}>
                          {LIST.to}
                          <input
                            type="date"
                            value={draft.to}
                            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                            style={inputStyle}
                          />
                        </label>
                        {draftProblem && (
                          <p role="alert" style={{ flexBasis: '100%', margin: 0, fontSize: 12, color: p.bad }}>{draftProblem}</p>
                        )}
                        <button
                          type="button"
                          onClick={applyCustom}
                          disabled={!!draftProblem}
                          style={{ ...buttonStyle(!!draftProblem), flexBasis: '100%' }}
                        >
                          {LIST.apply}
                        </button>
                      </div>
                    )}
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: p.muted }}>{LIST.dateRule}</p>
                  </div>
                )}
              </div>
            </>
          )}

          <select
            aria-label={LIST.kindLabel}
            value={query.kind}
            onChange={(e) => changeFilter({ kind: e.target.value })}
            style={selectStyle}
          >
            {Object.entries(LIST.kinds).map(([key, label]) => (
              <option key={key} value={key}>{withCount(label, kindCountOf(key))}</option>
            ))}
          </select>
          <select
            aria-label={LIST.statusLabel}
            value={query.status}
            onChange={(e) => changeFilter({ status: e.target.value })}
            style={selectStyle}
          >
            {LIST_STATUS_OPTIONS.map((key) => (
              <option key={key} value={key}>{withCount(LIST.statuses[key], statusCountOf(key))}</option>
            ))}
          </select>

          <button
            type="button"
            onClick={refresh}
            disabled={busy}
            aria-label={LIST.refresh}
            title={LIST.refresh}
            style={buttonStyle(busy)}
          >
            ↻
          </button>
        </div>
      </div>

      {/* OR-96 (TEMPORARY, with DEFAULT_PRESET): says why older visits are not on screen. Static: no request. The text
          token, not `muted` (4.3:1 on the page's indigo wash): plain solid text, no fill, readable in both themes. */}
      {!patientId && activePreset === 'last3' && (
        <p data-testid="last3-hint" style={{ margin: '0 0 10px', fontSize: 12, color: p.text }}>{LIST.last3Hint}</p>
      )}

      {linkNotice && (
        <div
          style={{
            background: noticeLook.bg, color: noticeLook.text, border: `1px solid ${noticeLook.border}`, borderRadius: 10,
            padding: '10px 14px', fontSize: 13, margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 12,
          }}
        >
          <span role="status" aria-live="polite" style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {noticeLines(linkNotice).map((line) => (
              <span key={line} style={{ display: 'block' }}>{line}</span>
            ))}
          </span>
          <button
            type="button"
            onClick={hideNotice}
            aria-label={LIST.dismissNotice}
            title={LIST.dismissNotice}
            style={{
              background: p.card, color: p.text, border: `1px solid ${noticeLook.border}`, borderRadius: 6, cursor: 'pointer',
              fontSize: 16, fontWeight: 700, lineHeight: '22px', padding: '0 9px', flex: 'none',
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      )}

      {failed && (
        <div role="alert" style={{ ...noteStyle, color: p.bad }}>
          <p>{LIST.loadError}</p>
          <button type="button" onClick={refresh} style={buttonStyle(false)}>Retry</button>
        </div>
      )}

      {!failed && busy && records.length === 0 && (
        <div style={noteStyle}>{LIST.loading}</div>
      )}

      {!failed && !busy && res && records.length === 0 && (
        <div style={noteStyle}>
          {patientId ? (
            <p style={{ margin: 0 }}>{PATIENT.empty}</p>
          ) : (
            <>
              <p style={{ margin: 0 }}>{(notLinkedYet ? LIST.emptyNotLinked : LIST.empty)(rangeText)}</p>
              <p style={{ margin: '4px 0 0', fontSize: 12 }}>{LIST.emptyHint}</p>
            </>
          )}
        </div>
      )}

      {!failed && records.length > 0 && (
        <>
          {!patientId && shown.status === LIST_STATUS.NOT_VERIFIED && (
            <div
              role="note"
              style={{
                background: p.warnBg, color: p.warnText, border: `1px solid ${p.warnBorder}`,
                borderRadius: 10, padding: '10px 14px', fontSize: 13, margin: '0 0 10px',
              }}
            >
              <b style={{ display: 'block' }}>{LIST.notVerifiedBanner(res.totalCount)}</b>
              {LIST.notVerifiedBannerBody}
            </div>
          )}

          <p style={{ margin: '0 0 6px', fontSize: 12, color: p.muted }}>{countText}</p>

          {/* What the record-type pills on the cards mean (the state is always in the words, never the colour alone). */}
          <p
            data-testid="list-legend"
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px', margin: '0 0 10px', fontSize: 12, color: p.muted }}
          >
            <b>{STRIP.legend.label}</b>
            <span style={legendPill({ background: p.inAppBg, color: p.inAppText, border: p.inAppBg })}>{STRIP.legend.inApp}</span>
            <span style={legendPill({ background: p.okBg, color: p.okText, border: p.okBorder })}>{STRIP.legend.linked}</span>
            <span style={legendPill({ background: p.card, color: p.text, border: p.border })}>{STRIP.legend.missing}</span>
            {/* The greyed chip wears the strip pill's own colours (NOT_APPLICABLE_LOOK), so it matches it in both themes (OR-88). */}
            <span data-testid="legend-not-applicable" className={`border ${NOT_APPLICABLE_LOOK}`} style={legendShape}>{STRIP.legend.notApplicable}</span>
            <span aria-hidden="true">·</span>
            <span>{STRIP.legend.tick}</span>
          </p>

          <div
            aria-busy={busy}
            style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: busy ? 0.6 : 1 }}
          >
            {patientId
              ? groupByMonth(records).map((g, i) => (
                // key by position too: a month can come back as a second run (rows shifting between pages)
                <section key={`${g.key}:${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <h2 style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 700, color: p.text }}>
                    {PATIENT.monthHeader(formatPeriodLabel(g.key), g.records.length)}
                  </h2>
                  {g.records.map(renderCard)}
                </section>
              ))
              : records.map(renderCard)}
          </div>

          {patientId && remaining > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 16 }}>
              <span style={{ fontSize: 13, color: p.muted }}>
                {PATIENT.shownOf(records.length, res.totalCount, formatDateIST(records[records.length - 1].encounterAt))}
              </span>
              <button type="button" onClick={showOlder} disabled={busy} style={buttonStyle(busy)}>
                {PATIENT.showOlder(Math.min(PATIENT_PAGE_SIZE, remaining))}
              </button>
            </div>
          )}

          {!patientId && totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => goToPage(res.page - 1)}
                disabled={busy || res.page <= 1}
                style={buttonStyle(busy || res.page <= 1)}
              >
                {LIST.previous}
              </button>
              <span style={{ fontSize: 13, color: p.muted }}>{LIST.pageOf(res.page, totalPages, res.totalCount)}</span>
              <button
                type="button"
                onClick={() => goToPage(res.page + 1)}
                disabled={busy || res.page >= totalPages}
                style={buttonStyle(busy || res.page >= totalPages)}
              >
                {LIST.next}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
